import { SignedIn, SignedOut, SignInButton, UserButton, useUser, useAuth } from '@clerk/clerk-react'
import { useState, useEffect } from 'react'
import DashboardV2 from './components/DashboardV2'
import AdminDashboard from './components/admin/AdminDashboard'
import ExecutiveSummary from './components/ExecutiveSummary'
import TenantSelector from './components/TenantSelector'
import { getApiUrl, navigateToTenantPortal, getAdminPortalUrl } from './config/api'
import { api } from './services/api'
import type { UserTenant } from './services/api'
import type { SecurityScore, RecommendationsResponse } from './types'

// Utility to extract tenant slug from subdomain or query param
function getTenantSlug(): string | null {
  const hostname = window.location.hostname
  const params = new URLSearchParams(window.location.search)

  // Check query param first (works in both dev and production)
  const tenantParam = params.get('tenant')
  if (tenantParam) {
    return tenantParam
  }

  // Local development - no subdomain support
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null
  }

  // Production: extract from subdomain
  const parts = hostname.split('.')

  // Format 1: {slug}-panel.jarix.io (custom domain)
  if (hostname.endsWith('.jarix.io')) {
    const subdomain = parts[0]
    if (subdomain.endsWith('-panel')) {
      // Extract slug from "{slug}-panel"
      return subdomain.replace(/-panel$/, '')
    }
  }

  // Format 2: {tenant}.jarix-dashboard.pages.dev (Cloudflare Pages)
  if (parts.length >= 3) {
    const subdomain = parts[0]
    // Skip if it's 'www' or 'admin' or the main domain
    if (subdomain !== 'www' && subdomain !== 'jarix-dashboard' && subdomain !== 'admin') {
      return subdomain
    }
  }

  return null
}

function isAdminSubdomain(): boolean {
  const hostname = window.location.hostname
  const params = new URLSearchParams(window.location.search)

  // Check query param first (works in both dev and production)
  if (params.get('admin') === 'true') {
    return true
  }

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return false
  }

  // Production: check for hub.jarix.io (admin panel)
  if (hostname === 'hub.jarix.io') {
    return true
  }

  // Legacy: check subdomain 'admin' for pages.dev
  const parts = hostname.split('.')
  return parts[0] === 'admin'
}

function isExecutiveView(): boolean {
  const params = new URLSearchParams(window.location.search)
  return params.get('view') === 'executive'
}

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jarix-coral mx-auto mb-4"></div>
        <p className="text-gray-400">Cargando...</p>
      </div>
    </div>
  )
}

// Sign in page component - Updated with Jarix branding
function SignInPage() {
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="bg-gray-800 p-8 rounded-card shadow-2xl max-w-md w-full mx-4">
        <div className="text-center mb-8">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img
              src="/favicon.png"
              alt="Jarix"
              className="w-16 h-16"
            />
          </div>
          <h1 className="font-heading text-3xl font-semibold text-white mb-2 tracking-tight">
            <span className="text-jarix-coral">Jarix</span> Security Portal
          </h1>
          <p className="text-gray-400 font-sans">Inicia sesion para acceder al dashboard</p>
        </div>

        <div className="flex justify-center">
          <SignInButton mode="modal">
            <button className="bg-jarix-coral hover:bg-jarix-coral-dark text-white font-semibold py-3 px-8 rounded-button transition-colors duration-200 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Iniciar Sesion
            </button>
          </SignInButton>
        </div>

        <p className="text-gray-500 text-sm text-center mt-6 font-sans">
          Acceso restringido a usuarios autorizados
        </p>
      </div>
    </div>
  )
}

// Executive Dashboard - simplified view for C-level
function ExecutiveDashboard({ tenantSlug }: { tenantSlug: string }) {
  const { getToken } = useAuth()
  const [loading, setLoading] = useState(true)
  const [securityScore, setSecurityScore] = useState<SecurityScore>({
    overall: 0,
    breakdown: { deviceProtection: 0, userSecurity: 0, passwordHealth: 0, deviceConnectivity: 0 },
    grade: 'F'
  })
  const [recommendations, setRecommendations] = useState<RecommendationsResponse | null>(null)
  const [alertsCount, setAlertsCount] = useState(0)
  const [criticalAlertsCount, setCriticalAlertsCount] = useState(0)

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get auth token from Clerk
        const token = await getToken()

        const apiUrl = getApiUrl(`/api/dashboard?timeRange=7d&tenant=${tenantSlug}`)
        const response = await fetch(apiUrl, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
        })
        if (response.ok) {
          interface DashboardData {
            securityScore?: SecurityScore
            recommendations?: RecommendationsResponse
            highRiskUsers?: Array<{ riskFactors?: Array<{ severity: string }> }>
            devicesWithIssues?: Array<{ severityLevel: string }>
          }
          const data: DashboardData = await response.json()
          if (data.securityScore) {
            setSecurityScore(data.securityScore)
          }
          if (data.recommendations) {
            setRecommendations(data.recommendations)
          }
          // Count alerts from high risk users and devices with issues
          const highRiskCount = data.highRiskUsers?.length || 0
          const deviceIssuesCount = data.devicesWithIssues?.length || 0
          setAlertsCount(highRiskCount + deviceIssuesCount)

          // Count critical alerts
          const criticalUsers = data.highRiskUsers?.filter(u =>
            u.riskFactors?.some(f => f.severity === 'critical')
          )?.length || 0
          const criticalDevices = data.devicesWithIssues?.filter(d =>
            d.severityLevel === 'critical'
          )?.length || 0
          setCriticalAlertsCount(criticalUsers + criticalDevices)
        }
      } catch (error) {
        console.error('Error fetching executive data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [tenantSlug])

  const handleViewDetails = () => {
    // Navigate to full dashboard
    const params = new URLSearchParams(window.location.search)
    params.delete('view')
    window.location.search = params.toString()
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 p-8">
      <div className="max-w-2xl mx-auto">
        <ExecutiveSummary
          score={securityScore}
          recommendations={recommendations}
          alertsCount={alertsCount}
          criticalAlertsCount={criticalAlertsCount}
          tenantName={tenantSlug}
          onViewDetails={handleViewDetails}
        />
      </div>
    </div>
  )
}

// Header with user info - Updated with Jarix branding
function AppHeader({ tenantName }: { tenantName?: string }) {
  const { user } = useUser()

  return (
    <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Jarix Logo */}
          <div className="flex items-center gap-3">
            <img
              src="/favicon.png"
              alt="Jarix"
              className="w-8 h-8"
            />
            <h1 className="font-heading text-xl font-semibold text-white tracking-tight">
              <span className="text-jarix-coral">Jarix</span> Security Portal
            </h1>
          </div>
          {tenantName && (
            <span className="bg-jarix-lavender/20 text-jarix-lavender-dark px-3 py-1 rounded-badge text-sm font-medium border border-jarix-lavender/30">
              {tenantName}
            </span>
          )}
        </div>

        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm font-sans">
            {user?.emailAddresses[0]?.emailAddress}
          </span>
          <UserButton
            afterSignOutUrl="/"
            appearance={{
              elements: {
                avatarBox: "w-10 h-10"
              }
            }}
          />
        </div>
      </div>
    </header>
  )
}

// Access denied component
function AccessDenied() {
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-8 text-center">
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Sin acceso</h2>
        <p className="text-gray-400 mb-6">
          No tienes acceso a ningun portal de cliente. Contacta al administrador si crees que esto es un error.
        </p>
        <a
          href="mailto:soporte@jarix.io"
          className="text-jarix-coral hover:text-jarix-coral-dark transition-colors"
        >
          soporte@jarix.io
        </a>
      </div>
    </div>
  )
}

// Main App component
function App() {
  const [tenantSlug, setTenantSlug] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [isExecutive, setIsExecutive] = useState(false)
  const [loading, setLoading] = useState(true)
  const [userTenants, setUserTenants] = useState<UserTenant[]>([])
  const [hasAdminAccess, setHasAdminAccess] = useState(false)
  // Global user.role from /api/auth/me. Distinct from tenant_users.role
  // which is per-tenant. Needed so a jarix_admin browsing a tenant
  // portal can mutate tenant-scoped settings (e.g. branding) — they
  // typically have no tenant_users row, so tenant_role is undefined.
  const [userRole, setUserRole] = useState<string | undefined>(undefined)
  const [authChecked, setAuthChecked] = useState(false)
  const { isLoaded, user } = useUser()
  const { isSignedIn } = useAuth()

  // Get URL parameters on mount
  useEffect(() => {
    setTenantSlug(getTenantSlug())
    setIsAdmin(isAdminSubdomain())
    setIsExecutive(isExecutiveView())
    setLoading(false)
  }, [])

  // Fetch user permissions after sign in
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!isSignedIn) {
        setAuthChecked(true)
        return
      }

      try {
        const result = await api.getMe()
        if (result.success && result.data?.user) {
          const userData = result.data.user
          // User has admin access if they have an adminRole (super_admin, admin, viewer)
          setHasAdminAccess(!!userData.adminRole)
          // Set user's accessible tenants
          setUserTenants(userData.tenants || [])
          setUserRole(userData.role)
        }
      } catch (error) {
        console.error('Error fetching user info:', error)
      } finally {
        setAuthChecked(true)
      }
    }

    if (isLoaded && isSignedIn) {
      fetchUserInfo()
    }
  }, [isLoaded, isSignedIn])

  // Wait for Clerk to load
  if (!isLoaded || loading) {
    return <LoadingSpinner />
  }

  return (
    <>
      <SignedOut>
        <SignInPage />
      </SignedOut>

      <SignedIn>
        {/* Wait for auth check to complete */}
        {!authChecked ? (
          <LoadingSpinner />
        ) : (
          <div className="min-h-screen bg-gray-900">
            {/* Case 1: Admin portal requested */}
            {isAdmin ? (
              hasAdminAccess ? (
                <>
                  <AppHeader tenantName="Admin" />
                  <main>
                    <AdminDashboard />
                  </main>
                </>
              ) : (
                // No admin access - show access denied
                <AccessDenied />
              )
            ) : tenantSlug ? (
              // Case 2: Specific tenant requested
              isExecutive ? (
                <>
                  <AppHeader tenantName={tenantSlug} />
                  <main>
                    <ExecutiveDashboard tenantSlug={tenantSlug} />
                  </main>
                </>
              ) : (
                <DashboardV2
                  tenantSlug={tenantSlug}
                  tenants={userTenants}
                  userRole={userRole}
                  onTenantChange={(slug) => {
                    navigateToTenantPortal(slug)
                  }}
                />
              )
            ) : userTenants.length === 1 ? (
              // Case 3: User has exactly 1 tenant - redirect automatically
              (() => {
                navigateToTenantPortal(userTenants[0].slug)
                return <LoadingSpinner />
              })()
            ) : userTenants.length > 1 ? (
              // Case 4: User has multiple tenants - show selector
              <TenantSelector
                tenants={userTenants}
                userName={user?.firstName || user?.emailAddresses[0]?.emailAddress}
              />
            ) : hasAdminAccess ? (
              // Case 5: Admin user with no tenants - redirect to admin portal
              (() => {
                window.location.href = getAdminPortalUrl()
                return <LoadingSpinner />
              })()
            ) : (
              // Case 6: No tenants and no admin access
              <AccessDenied />
            )}
          </div>
        )}
      </SignedIn>
    </>
  )
}

export default App
